// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.
import {
  useCallback,
  useEffect,
  useInsertionEffect,
  useMemo,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { gestures } from "@/components/wingo/lib/motion";

export type LongPressPointerType = "touch" | "pen" | "mouse";

// where and how the press happened, handed to the callback when it fires
export type LongPressEvent<T extends Element = Element> = {
  target: T;
  clientX: number;
  clientY: number;
  pointerType: LongPressPointerType;
};

export type UseLongPressOptions<T extends Element = Element> = {
  // hold time in ms before it fires (default gestures.longPress, 450)
  delay?: number;
  // movement in px that cancels the press, so scrolling never fires it (default gestures.slop, 8)
  slop?: number;
  disabled?: boolean;
  // pointers that can long press. default touch and pen: a mouse has right-click for the same job
  pointerTypes?: readonly LongPressPointerType[];
  // a short vibration when it fires, where the device supports it (default true)
  haptics?: boolean;
  // a press started (drive a "holding" style from here)
  onStart?: (event: LongPressEvent<T>) => void;
  // the press ended before firing: released early, moved, scrolled or cancelled
  onCancel?: () => void;
  // the finger lifted after the long press fired
  onFinish?: () => void;
};

export type LongPressHandlers<T extends Element = Element> = {
  onPointerDown: (event: ReactPointerEvent<T>) => void;
  onPointerMove: (event: ReactPointerEvent<T>) => void;
  onPointerUp: (event: ReactPointerEvent<T>) => void;
  onPointerCancel: (event: ReactPointerEvent<T>) => void;
  onPointerLeave: (event: ReactPointerEvent<T>) => void;
  onContextMenu: (event: ReactMouseEvent<T>) => void;
  onClickCapture: (event: ReactMouseEvent<T>) => void;
};

const DEFAULT_POINTERS: readonly LongPressPointerType[] = ["touch", "pen"];

// press and hold: returns pointer handlers to spread on the element (or on a Radix trigger through asChild, which
// merges them with its own). the click that follows a long press is swallowed, and the browser's own long-press
// menu is suppressed. give the element `select-none [-webkit-touch-callout:none]` so ios shows no callout
export function useLongPress<T extends Element = Element>(
  onLongPress: (event: LongPressEvent<T>) => void,
  {
    delay = gestures.longPress,
    slop = gestures.slop,
    disabled = false,
    pointerTypes = DEFAULT_POINTERS,
    haptics = true,
    onStart,
    onCancel,
    onFinish,
  }: UseLongPressOptions<T> = {},
): LongPressHandlers<T> {
  const callbacks = useRef({ onLongPress, onStart, onCancel, onFinish });
  useInsertionEffect(() => {
    callbacks.current = { onLongPress, onStart, onCancel, onFinish };
  });

  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const origin = useRef<{ x: number; y: number; id: number } | null>(null);
  const fired = useRef(false);

  const clear = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  useEffect(() => clear, [clear]);

  const cancel = useCallback(() => {
    if (!origin.current) return;
    const wasPending = timer.current !== undefined;
    clear();
    origin.current = null;
    if (wasPending) callbacks.current.onCancel?.();
  }, [clear]);

  const types = pointerTypes.join(",");

  return useMemo<LongPressHandlers<T>>(() => {
    const accepts = (pointerType: string) => types.split(",").includes(pointerType);

    return {
      onPointerDown(event) {
        if (disabled || !accepts(event.pointerType)) return;
        // only the primary button of a mouse or pen
        if (event.pointerType !== "touch" && event.button !== 0) return;
        clear();
        fired.current = false;
        origin.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
        const info: LongPressEvent<T> = {
          target: event.currentTarget,
          clientX: event.clientX,
          clientY: event.clientY,
          pointerType: event.pointerType as LongPressPointerType,
        };
        callbacks.current.onStart?.(info);
        timer.current = setTimeout(() => {
          timer.current = undefined;
          fired.current = true;
          if (haptics) {
            try {
              navigator.vibrate?.(10);
            } catch {
              // vibrate throws in some embedded browsers; the press still counts
            }
          }
          callbacks.current.onLongPress(info);
        }, delay);
      },
      onPointerMove(event) {
        const start = origin.current;
        if (!start || start.id !== event.pointerId || timer.current === undefined) return;
        if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > slop) cancel();
      },
      onPointerUp() {
        if (!origin.current) return;
        if (fired.current) {
          origin.current = null;
          callbacks.current.onFinish?.();
        } else cancel();
      },
      onPointerCancel() {
        if (fired.current && origin.current) {
          origin.current = null;
          callbacks.current.onFinish?.();
        } else cancel();
      },
      onPointerLeave(event) {
        // touch pointers "leave" when the finger lifts; the up handler already ran
        if (event.pointerType === "touch") return;
        if (!fired.current) cancel();
      },
      onContextMenu(event) {
        // android and desktop touch screens open the native menu on a long press; ours replaces it
        if (origin.current || fired.current) event.preventDefault();
      },
      onClickCapture(event) {
        if (!fired.current) return;
        fired.current = false;
        // a keyboard click (detail 0) is never the tail of a press
        if (event.detail === 0) return;
        event.preventDefault();
        event.stopPropagation();
      },
    };
  }, [disabled, types, delay, slop, haptics, clear, cancel]);
}
